import { useEffect, useRef, useState } from "react";
import css from "./TimePicker.module.css";

/**
 * Модель времени TimePicker + прямой ввод с клавиатуры.
 * Вынесено из TimePicker.tsx (лимит новых файлов ≤250 строк).
 * Семантика — эталон design-references/time-picker.js:122-149.
 */
export interface Seg {
  h: number;
  m: number;
}

export const pad = (n: number) => String(n).padStart(2, "0");

export const toMin = (s: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

const mod = (n: number, l: number) => ((n % l) + l) % l;

/* Как в эталоне (TP:123): округление минут по mod 60, без переноса часа. */
export const roundStep = (m: number, step: number) => mod(Math.round(m / step) * step, 60);

export const fmtSeg = (s: Seg) => `${pad(s.h)}:${pad(s.m)}`;
export const segMin = (s: Seg) => s.h * 60 + s.m;

function parseOne(s: string | undefined, step: number): Seg {
  const t = toMin((s ?? "").trim());
  if (t === null) return { h: 0, m: 0 };
  return { h: Math.floor(t / 60), m: roundStep(t % 60, step) };
}

/** Парс "HH:MM" / "HH:MM–HH:MM" (en-dash или дефис); одиночное значение дублируется. */
export function initVals(value: string | undefined, step: number): [Seg, Seg] {
  const parts = String(value ?? "").split(/[–-]/);
  const a = parseOne(parts[0], step);
  const b = parts.length > 1 ? parseOne(parts[1], step) : { ...a };
  return [a, b];
}

/**
 * Прямой ввод по тапу на превью: numeric, авто-двоеточие, Enter = принять
 * (.bad при t>=1440), Esc/blur = отмена. Esc гасит всплытие — отменяется
 * только ввод, а не весь пикер (осознанный фикс квирка эталона TP:118).
 */
export function DirectInput({
  seg,
  step,
  onDone,
}: {
  seg: Seg;
  step: number;
  onDone: (s: Seg | null) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const doneRef = useRef(false);
  const [text, setText] = useState(() => fmtSeg(seg));
  const [bad, setBad] = useState(false);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const finish = (s: Seg | null) => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone(s);
  };
  return (
    <input
      ref={ref}
      className={`${css.previnp} ${bad ? css.bad : ""}`}
      inputMode="numeric"
      value={text}
      onChange={(e) => {
        setBad(false);
        const d = e.target.value.replace(/\D/g, "").slice(0, 4);
        setText(d.length > 2 ? `${d.slice(0, 2)}:${d.slice(2)}` : d);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          const t = toMin(text.length === 4 ? `0${text}` : text);
          if (t === null || t >= 1440) {
            setBad(true);
            return;
          }
          finish({ h: Math.floor(t / 60), m: roundStep(t % 60, step) });
        } else if (e.key === "Escape") {
          e.stopPropagation();
          finish(null);
        }
      }}
      onBlur={() => finish(null)}
    />
  );
}
